import { useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/Modals/Modal/Modal';
import { Input } from 'components/Input/InputFormik';
import Button from 'components/Button/Button';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useCurrencySymbol } from 'utils/hooks/useFormatMoney';
import { Level } from 'components/Modals/Modal/type';
import { slugify } from 'utils/stringUtils';
import {
  ICreateOptionData,
  IUpdateOptionData,
} from 'types/ConfigurationQuestion.types';
import { optionFormSchema } from '../validation';
import {
  ButtonRow,
  FieldLabel,
  FormColumn,
  FormModalContainer,
  HelperText,
  Title,
} from './OptionFormModal.styled';

interface IOptionFormModalProps {
  isOpen: boolean;
  level: Level;
  currency: string;
  // Structural shape so both server options and local (test) options reuse this form.
  optionForEditing: {
    label: string;
    slug: string;
    price: number;
    is_default: boolean;
  } | null;
  onClose: () => void;
  onAdd: (data: ICreateOptionData) => void;
  onEdit: (data: IUpdateOptionData) => void;
  isLoading: boolean;
}

interface IOptionFormValues {
  label: string;
  slug: string;
  price: string | number;
  is_default: boolean;
}

export const OptionFormModal = ({
  isOpen,
  level,
  currency,
  optionForEditing,
  onClose,
  onAdd,
  onEdit,
  isLoading,
}: IOptionFormModalProps) => {
  const { t } = useTranslation();
  const { isPhone } = useBreakpointFlag();
  const currencySymbol = useCurrencySymbol(currency);
  const isEdit = !!optionForEditing;
  const slugManuallyEditedRef = useRef<boolean>(false);

  return (
    <Modal
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      modalStyle={{
        margin: 'auto',
        width: isPhone ? '90%' : '500rem',
        minWidth: isPhone ? '90%' : 'auto',
        position: 'fixed',
      }}
    >
      <FormModalContainer>
        <Title>{isEdit ? t('Edit option') : t('Add option')}</Title>
        <Formik
          enableReinitialize
          initialValues={{
            label: optionForEditing?.label || '',
            slug: optionForEditing?.slug || '',
            price: optionForEditing?.price ?? '',
            is_default: optionForEditing?.is_default || false,
          }}
          validationSchema={optionFormSchema}
          validateOnChange
          validateOnMount
          onSubmit={(values: IOptionFormValues) => {
            if (isEdit) {
              onEdit({
                label: values.label.trim(),
                price: Number(values.price),
                is_default: values.is_default,
              });
            } else {
              onAdd({
                label: values.label.trim(),
                slug: values.slug.trim(),
                price: Number(values.price),
              });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isValid,
            setFieldValue,
            submitForm,
          }: FormikProps<IOptionFormValues>) => (
            <>
              <FormColumn>
                <FieldLabel>{t('Label')}</FieldLabel>
                <Input
                  name="label"
                  height={'41rem'}
                  placeholder={t('Label')}
                  wrapperStyles={{ width: '100%' }}
                  handleOnChange={(value) => {
                    if (!isEdit && !slugManuallyEditedRef.current) {
                      setFieldValue('slug', slugify(value));
                    }
                  }}
                  errorMessage={
                    touched.label && errors.label ? t(errors.label) : ''
                  }
                />
              </FormColumn>
              <FormColumn>
                <FieldLabel>{t('Slug')}</FieldLabel>
                <Input
                  name="slug"
                  height={'41rem'}
                  placeholder={t('slug')}
                  disabled={isEdit}
                  wrapperStyles={{ width: '100%' }}
                  handleOnChange={() => {
                    slugManuallyEditedRef.current = true;
                  }}
                  errorMessage={
                    !isEdit && touched.slug && errors.slug ? t(errors.slug) : ''
                  }
                />
                <HelperText>
                  {isEdit
                    ? t('The slug cannot be changed after creation')
                    : t(
                        'Lowercase letters, numbers and hyphens only. Must start with a letter.'
                      )}
                </HelperText>
              </FormColumn>
              <FormColumn>
                <FieldLabel>{t('Price')}</FieldLabel>
                <Input
                  name="price"
                  height={'41rem'}
                  type="number"
                  placeholder={t('Price')}
                  symbol={currencySymbol}
                  wrapperStyles={{ width: '100%' }}
                  errorMessage={
                    touched.price && errors.price ? t(errors.price) : ''
                  }
                />
              </FormColumn>
              {isEdit && (
                <Checkbox
                  label={t('Set as default')}
                  isChecked={values.is_default}
                  onChange={() =>
                    setFieldValue('is_default', !values.is_default)
                  }
                />
              )}
              <ButtonRow>
                <Button
                  secondary
                  label={t('Cancel')}
                  width={isPhone ? '120rem' : '200rem'}
                  onClick={onClose}
                />
                <Button
                  primary
                  disabled={!isValid || isLoading}
                  label={isEdit ? t('Save') : t('Add')}
                  width={isPhone ? '120rem' : '200rem'}
                  onClick={submitForm}
                />
              </ButtonRow>
            </>
          )}
        </Formik>
      </FormModalContainer>
    </Modal>
  );
};
